import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MonthlySchedules from './pages/MonthlySchedules';
import DailyDispatches from './pages/DailyDispatches';
import Production from './pages/Production';
import Customers from './pages/Customers';
import Users from './pages/Users';
import Settings from './pages/Settings';
import JobWorkVendors from './pages/JobWorkVendors';
import JobWorkDashboard from './pages/JobWorkDashboard';
import JobWorkPOs from './pages/JobWorkPOs';
import JobWorkChallans from './pages/JobWorkChallans';
import JobWorkBalance from './pages/JobWorkBalance';
import QualityInspection from './pages/QualityInspection';
import SendMaterial from './pages/SendMaterial';
import ReceiveMaterial from './pages/ReceiveMaterial';
import VendorPerformance from './pages/VendorPerformance';
import PartsMaster from './pages/PartsMaster';
import HelpCenter from './pages/HelpCenter';
import Layout from './components/Layout';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isSmallScreen = window.innerWidth < 1024;
      setIsMobile(mobileRegex.test(userAgent) || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Mobile Block Screen Component
const MobileBlockScreen = () => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
      <div className="text-6xl mb-4">🖥️</div>
      <h1 className="text-2xl font-bold text-gray-900 mb-3">Desktop Access Only</h1>
      <p className="text-gray-600 mb-4">
        This ERP system is designed for desktop use only. Please access it from a computer or laptop for the best experience.
      </p>
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500">
        <p className="font-medium text-gray-700 mb-2">Minimum Requirements:</p>
        <ul className="text-left space-y-1">
          <li>• Screen width: 1024px or larger</li>
          <li>• Desktop or Laptop computer</li>
          <li>• Modern web browser</li>
        </ul>
      </div>
    </div>
  </div>
);

// Setup axios interceptor for auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Session expired event for cross-component communication
const SESSION_EXPIRED_EVENT = 'session_expired';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    checkAuth();
    
    // Listen for session expired events
    const handleSessionExpired = (e) => {
      setSessionExpiredMsg(e.detail || 'You have been logged in from another device.');
      localStorage.removeItem('token');
      setUser(null);
    };
    
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);
  
  // Axios response interceptor to catch session expiry
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && 
            error.response?.data?.detail?.includes('logged in from another device')) {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: error.response.data.detail
          }));
        }
        return Promise.reject(error);
      }
    );
    
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setSessionExpiredMsg(null); // Clear any session expired message
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Block mobile access for non-admin users
  if (isMobile && user && user.role !== 'admin') {
    return <MobileBlockScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={!user ? <Login onLogin={handleLogin} sessionExpiredMsg={sessionExpiredMsg} /> : <Navigate to="/" />}
        />
        <Route
          path="/"
          element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        >
          <Route index element={<Dashboard />} />
          <Route path="schedules" element={<MonthlySchedules />} />
          <Route path="dispatches" element={<DailyDispatches />} />
          <Route path="production" element={<Production />} />
          <Route path="jobwork-dashboard" element={<JobWorkDashboard />} />
          <Route path="jobwork-vendors" element={<JobWorkVendors />} />
          <Route path="jobwork-pos" element={<JobWorkPOs />} />
          <Route path="jobwork-challans" element={<JobWorkChallans />} />
          <Route path="jobwork-balance" element={<JobWorkBalance />} />
          <Route path="quality-inspection" element={<QualityInspection />} />
          <Route path="send-material" element={<SendMaterial />} />
          <Route path="receive-material" element={<ReceiveMaterial />} />
          <Route path="vendor-performance" element={<VendorPerformance />} />
          <Route path="parts-master" element={<PartsMaster />} />
          <Route path="customers" element={<Customers />} />
          <Route path="help" element={<HelpCenter />} />
          {/* Admin-only routes with proper guards */}
          <Route 
            path="settings" 
            element={user?.role === 'admin' ? <Settings /> : <Navigate to="/" replace />} 
          />
          <Route 
            path="users" 
            element={user?.role === 'admin' ? <Users /> : <Navigate to="/" replace />} 
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
